import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Space, Table, Typography, message } from 'antd';
import { BellOutlined, ReloadOutlined } from '@ant-design/icons';
import request from '../../api/request';
import { createTaskColumns } from './settingsColumns';
import SyncTaskEditModal from './SyncTaskEditModal';
import SyncTaskRunsDrawer from './SyncTaskRunsDrawer';
import type { RunTaskResponse, SyncTask, SyncTaskRun } from './types';

const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<SyncTask[]>([]);
  const [editingTask, setEditingTask] = useState<SyncTask | null>(null);
  const [editVisible, setEditVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runsVisible, setRunsVisible] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsTask, setRunsTask] = useState<SyncTask | null>(null);
  const [runs, setRuns] = useState<SyncTaskRun[]>([]);
  const [testNotificationLoading, setTestNotificationLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request.get<any, SyncTask[]>('/api/sync-tasks');
      setTasks(Array.isArray(data) ? data : []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取同步任务失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRuns = useCallback(async (taskId: number) => {
    setRunsLoading(true);
    try {
      const data = await request.get<any, SyncTaskRun[]>(`/api/sync-tasks/${taskId}/runs`, {
        params: { limit: 20 },
      });
      setRuns(Array.isArray(data) ? data : []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取执行记录失败');
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    const timer = window.setInterval(() => {
      fetchTasks();
    }, 15000);
    return () => window.clearInterval(timer);
  }, [fetchTasks]);

  const openEditModal = (task: SyncTask) => {
    setEditingTask(task);
    setEditVisible(true);
    form.setFieldsValue({
      enabled: task.enabled,
      intervalMinutes: task.intervalMinutes,
      batchLimit: task.batchLimit,
      lookbackDays: task.lookbackDays,
      maxRetryCount: task.maxRetryCount || 0,
    });
  };

  const closeEditModal = () => {
    setEditVisible(false);
    setEditingTask(null);
    form.resetFields();
  };

  const handleEditSubmit = async () => {
    if (!editingTask) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await request.patch(`/api/sync-tasks/${editingTask.id}`, values);
      message.success('任务配置已更新');
      closeEditModal();
      await fetchTasks();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '更新任务配置失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleEnabled = async (task: SyncTask, enabled: boolean) => {
    try {
      await request.patch(`/api/sync-tasks/${task.id}`, { enabled });
      message.success(enabled ? '任务已启用' : '任务已停用');
      await fetchTasks();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '更新任务状态失败');
    }
  };

  const handleRunTask = async (task: SyncTask) => {
    try {
      const result = await request.post<any, RunTaskResponse>(`/api/sync-tasks/${task.id}/run`);
      message.success(result?.message || '任务已开始执行');
      await fetchTasks();
      if (runsTask?.id === task.id) await fetchRuns(task.id);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '执行任务失败');
    }
  };

  const openRunsDrawer = async (task: SyncTask) => {
    setRunsTask(task);
    setRunsVisible(true);
    await fetchRuns(task.id);
  };

  const closeRunsDrawer = () => {
    setRunsVisible(false);
    setRunsTask(null);
    setRuns([]);
  };

  const handleTestNotification = async () => {
    setTestNotificationLoading(true);
    try {
      const result = await request.post<any, { success?: boolean; message?: string }>('/api/sync-tasks/test-notification');
      message.success(result?.message || '测试通知已发送');
    } catch (error: any) {
      message.error(error?.response?.data?.message || '测试通知发送失败');
    } finally {
      setTestNotificationLoading(false);
    }
  };

  const failedTasks = tasks.filter((task) => task.lastRunStatus === 'failed' || (task.failureStreak || 0) > 0);
  const taskColumns = createTaskColumns({
    onToggleEnabled: handleToggleEnabled,
    onEdit: openEditModal,
    onRun: (task) => void handleRunTask(task),
    onOpenRuns: (task) => void openRunsDrawer(task),
  });

  return (
    <>
      <Card
        title="同步任务管理"
        extra={<Button icon={<ReloadOutlined />} onClick={() => void fetchTasks()}>刷新</Button>}
      >
        <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 16 }}>
          <Typography.Text type="secondary">
            在这里统一管理订单状态、自动重提和产品代理同步任务。修改开关和频率后，无需重启服务即可生效。
          </Typography.Text>
          <Space wrap>
            <Button type="primary" icon={<BellOutlined />} loading={testNotificationLoading} onClick={() => void handleTestNotification()}>
              测试飞书通知
            </Button>
            <Typography.Text type="secondary">用当前后端飞书配置发送一条测试消息，确认机器人可用。</Typography.Text>
          </Space>
        </Space>

        {failedTasks.length > 0 ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={`当前有 ${failedTasks.length} 个同步任务存在失败记录`}
            description={failedTasks.map((task) => `${task.taskName}：连续失败 ${task.failureStreak || 0} 次，${task.lastRunMessage || '请查看执行记录'}`).join('；')}
          />
        ) : null}

        <Table rowKey="id" columns={taskColumns} dataSource={tasks} loading={loading} size="small" scroll={{ x: 1400 }} pagination={false} />
      </Card>

      <SyncTaskEditModal
        open={editVisible}
        task={editingTask}
        form={form}
        submitting={submitting}
        onOk={() => void handleEditSubmit()}
        onCancel={closeEditModal}
      />

      <SyncTaskRunsDrawer
        open={runsVisible}
        task={runsTask}
        runs={runs}
        loading={runsLoading}
        onClose={closeRunsDrawer}
        onRefresh={(taskId) => void fetchRuns(taskId)}
        onRun={(task) => void handleRunTask(task)}
      />
    </>
  );
};

export default SettingsPage;
